import { forwardRef } from 'react';

import Box from '@mui/material/Box';
import NoSsr from '@mui/material/NoSsr';
import { useTheme } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

// The mark is drawn on a 24-unit grid: a `primary.main` tile holding an A whose crossbar runs
// past both legs, so the letter reads as an axis.
//
// It's SVG rather than an asset because the app asks for the logo in two different shapes —
// square (48x48 on sign-in, 64x64 on the splash screen) and horizontal (112x32 in the drawer,
// 80x40 in the footer). The previous `logo-single.png` was one bitmap with a black background
// baked in, letterboxed into both, which is why it read as a black tile on the white form card.
const MARK_PATHS = (
  <>
    <path d="M12 5.6 6.3 18.6" />
    <path d="M12 5.6 17.7 18.6" />
    <path d="M5.2 13.6h13.6" />
  </>
);

const TILE_RADIUS = 5.5;

const STROKE_WIDTH = 2.2;

// Boxes wider than 3:2 get the wordmark beside the tile; squarer ones get the tile alone.
const LOCKUP_RATIO = 1.6;

export const Logo = forwardRef(
  ({ width = 80, height = 40, disableLink = false, className, href = '/', sx, ...other }, ref) => {
    const theme = useTheme();

    const isLockup = width / height >= LOCKUP_RATIO;

    const tileColor = theme.vars.palette.primary.main;

    const letterColor = theme.vars.palette.common.white;

    const renderTile = (offsetY = 0) => (
      <>
        <rect y={offsetY} width="24" height="24" rx={TILE_RADIUS} fill={tileColor} />
        <g
          fill="none"
          stroke={letterColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          transform={offsetY ? `translate(0 ${offsetY})` : undefined}
        >
          {MARK_PATHS}
        </g>
      </>
    );

    // The wordmark is `<text>` in the UI's own font stack, not outlined paths: no font file to
    // ship, and it can't drift from the rest of the interface.
    const logo = isLockup ? (
      <Box component="svg" viewBox="0 0 112 32" width={width} height={height} aria-hidden="true">
        {renderTile(4)}
        <text
          x="33"
          y="22"
          fontSize="17"
          fontWeight="600"
          letterSpacing="1.7"
          fill={theme.vars.palette.text.primary}
          fontFamily={theme.typography.fontFamily}
        >
          AXION
        </text>
      </Box>
    ) : (
      <Box component="svg" viewBox="0 0 24 24" width={width} height={height} aria-hidden="true">
        {renderTile()}
      </Box>
    );

    return (
      <NoSsr
        fallback={
          <Box
            width={width}
            height={height}
            className={logoClasses.root.concat(className ? ` ${className}` : '')}
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              verticalAlign: 'middle',
              ...sx,
            }}
          />
        }
      >
        <Box
          ref={ref}
          component={RouterLink}
          href={href}
          width={width}
          height={height}
          className={logoClasses.root.concat(className ? ` ${className}` : '')}
          aria-label="Axion"
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            verticalAlign: 'middle',
            ...(disableLink && { pointerEvents: 'none' }),
            ...sx,
          }}
          {...other}
        >
          {logo}
        </Box>
      </NoSsr>
    );
  }
);
